import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TrendingDown, Fuel, Leaf, Gauge, Route, Truck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { today } from "@/lib/constants";

function Kpi({ icon: Icon, label, value, unit, sub, accent, testid }) {
  return (
    <Card className="p-4 flex items-start gap-3 rounded-md shadow-none" data-testid={testid}>
      <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${accent ? "bg-primary/10" : "bg-secondary"}`}>
        <Icon className={`h-5 w-5 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-display text-xl font-bold">{value}{unit && <span className="text-sm font-medium text-muted-foreground ml-1">{unit}</span>}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </Card>
  );
}

export default function Statistiques() {
  const firstOfMonth = today().slice(0, 8) + "01";
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stats/savings", { params: { start, end } });
      setData(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="stats-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <TrendingDown className="h-6 w-6 text-primary" /> Statistiques & Économies
        </h1>
        <p className="text-muted-foreground text-sm">Impact du dispatch IA : kilomètres à vide évités, carburant et CO₂ économisés.</p>
      </div>

      <Card className="p-4 rounded-md shadow-none">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Du</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="stats-start" /></div>
          <div><Label>Au</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="stats-end" /></div>
          <Button onClick={load} disabled={loading} data-testid="stats-refresh">{loading ? "Calcul…" : "Actualiser"}</Button>
        </div>
      </Card>

      {data && (
        <>
          <Card className="p-5 rounded-md shadow-none bg-primary/5 border-primary/20" data-testid="stats-hero">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-primary" />
              <h2 className="font-display text-lg font-semibold">Grâce à l'optimisation des retours à vide</h2>
            </div>
            <div className="flex flex-wrap items-end gap-x-10 gap-y-3 mt-3">
              <div>
                <p className="font-display text-4xl font-extrabold text-primary">{data.saved_empty_km} <span className="text-lg">km</span></p>
                <p className="text-xs text-muted-foreground">à vide évités</p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold">{data.fuel_saved_l} <span className="text-base">L</span></p>
                <p className="text-xs text-muted-foreground">carburant économisé</p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold">{data.co2_saved_kg} <span className="text-base">kg</span></p>
                <p className="text-xs text-muted-foreground">CO₂ évité</p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold text-primary" data-testid="stats-eur-hero">≈ {data.eur_saved} <span className="text-base">€</span></p>
                <p className="text-xs text-muted-foreground">carburant économisé</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Estimation : km à vide « naïfs » (départ dépôt → prise en charge) {data.baseline_empty_km} km, ramenés à {data.empty_km} km réels par le dispatch.
            </p>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={Route} label="Courses dispatchées" value={data.rides_count} testid="stats-rides" />
            <Kpi icon={Truck} label="Véhicules mobilisés" value={data.vehicles_used} testid="stats-vehicles" />
            <Kpi icon={Gauge} label="Taux de remplissage" value={data.occupancy_pct} unit="%" sub="km utiles / km totaux" accent testid="stats-occupancy" />
            <Kpi icon={Route} label="Km chargés" value={data.loaded_km} unit="km" testid="stats-loaded" />
            <Kpi icon={TrendingDown} label="Km à vide réels" value={data.empty_km} unit="km" testid="stats-empty" />
            <Kpi icon={Fuel} label="Carburant total estimé" value={data.fuel_total_l} unit="L" sub="0,12 L/km" testid="stats-fuel" />
            <Kpi icon={Leaf} label="CO₂ économisé" value={data.co2_saved_kg} unit="kg" accent testid="stats-co2" />
            <Kpi icon={Fuel} label="€ carburant économisé" value={data.eur_saved} unit="€" accent testid="stats-eur" />
            <Kpi icon={Sparkles} label="Trajets à vide mutualisés" value={data.empty_legs_shared} sub="via la bourse IA" testid="stats-shared" />
            <Kpi icon={Route} label="Distance totale" value={data.total_km} unit="km" testid="stats-total" />
          </div>
        </>
      )}
    </div>
  );
}
